import clsx from 'clsx';
interface ISegmentOption<T extends string> {
  label: string;
  value: T;
  disabled?: boolean;
  icon?: React.ElementType;
}

interface ISegmentedControlProps<T extends string> {
  options: ISegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  fullWidth?: boolean;
  variant?: 'pill' | 'underline';
}

export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  className,
  fullWidth = false,
  variant = 'pill',
}: ISegmentedControlProps<T>) => {
  return (
    <div
      className={clsx(
        variant === 'pill'
          ? 'inline-flex rounded-xl bg-neutral-light-100 p-1'
          : 'flex border-b border-neutral-light-200',
        fullWidth && 'w-full',
        className
      )}
      role="tablist"
    >
      {options.map((opt) => {
        const isActive = value === opt.value;

        return (
          <button
            key={opt?.value}
            type="button"
            onClick={() => !opt?.disabled && onChange(opt?.value)}
            disabled={opt?.disabled}
            role="tab"
            aria-selected={isActive}
            aria-disabled={opt?.disabled}
            className={clsx(
              'relative inline-flex items-center justify-center gap-2 text-sm font-semibold transition-all',
              fullWidth && 'flex-1',

              /* ===== PILL STYLE ===== */
              variant === 'pill' && [
                'px-4 py-2 rounded-lg',
                isActive
                  ? 'bg-brand-default text-white shadow-medium'
                  : 'text-neutral-secondary hover:text-neutral-primary',
              ],

              /* ===== UNDERLINE STYLE ===== */
              variant === 'underline' && [
                'px-4 py-3',
                isActive
                  ? 'text-brand-default'
                  : 'text-neutral-500 hover:text-neutral-primary',
              ],

              opt?.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            )}
          >
            {opt?.icon && (
              <opt.icon
                size={20}
                stroke={1.75}
                className={clsx(
                  variant === 'pill' && !isActive && 'text-neutral-secondary'
                )}
              />
            )}
            {opt?.label}

            {/* ===== UNDERLINE INDICATOR ===== */}
            {variant === 'underline' && isActive && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-default rounded-t-full" />
            )}
          </button>
        );
      })}
    </div>
  );
};
